/**
 * API istemcisi — sunucuya giden TEK kapı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DÖRT İŞ, TEK YERDE
 *
 * 1. Oturum çerezi her istekte gider (`credentials: 'include'`).
 * 2. Sunucunun `{ error: 'kod' }` cevabı TİPLİ bir hataya çevrilir.
 * 3. Kod → TÜRKÇE metin eşlemesi burada. Ekranlarda dağınık olsaydı aynı
 *    hata farklı yerlerde farklı kelimelerle anlatılırdı — "görsel ve dilsel
 *    tutarlılık" isteğinin en sık bozulduğu yer budur.
 * 4. Oturum ORTA YERDE düşerse haber verilir (S37) — aşağıya bakın.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Sunucunun hata sözleşmesi: `reply.code(4xx).send({ error: 'kod' })`. */
interface ErrorBody {
  readonly error?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  /** Sunucunun makine okunur kodu — `invalid_credentials` gibi. */
  readonly code: string;

  constructor(status: number, code: string) {
    super(mesaj(code, status));
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** Oturum düştü mü? Kabuk buna bakıp girişe yönlendirir. */
  get oturumsuz(): boolean {
    return this.status === 401 && this.code === 'unauthenticated';
  }

  /**
   * Operatör hiçbir firma seçmemiş — yazma işlemi bu yüzden reddedildi.
   *
   * Bu bir HATA DEĞİL, eksik bir adımdır: kullanıcı profilden firma seçince
   * aynı istek çalışır. Ekranlar bunu genel kırmızı hata yerine eylemli bir
   * uyarı olarak göstersin diye ayrı duruyor [K-010].
   */
  get firmaSecilmedi(): boolean {
    return this.status === 409 && this.code === 'no_active_firm';
  }
}

/**
 * Kod → kullanıcıya gösterilecek metin.
 *
 * Bilinmeyen kod geldiğinde HAM KOD gösterilmez; genel bir metin döner ama
 * kod `ApiError.code` içinde durmaya devam eder. Kullanıcıya
 * "invalid_totp" yazmak yardımcı olmaz; geliştiriciye lazım olan yerde durur.
 */
const METINLER: Record<string, string> = {
  invalid_body: 'Girilen bilgiler eksik veya hatalı.',
  invalid_credentials: 'E-posta veya parola hatalı.',
  totp_required: 'Doğrulama kodu gerekli.',
  invalid_totp: 'Doğrulama kodu hatalı.',
  too_many_attempts: 'Çok fazla deneme yapıldı. Bir süre sonra tekrar deneyin.',
  unauthenticated: 'Oturumunuz sona erdi. Tekrar giriş yapın.',
  operator_only: 'Bu işlem için yetkiniz yok.',
  forbidden: 'Bu işlem için yetkiniz yok.',
  firm_not_found: 'Firma bulunamadı.',
  invalid_current_password: 'Mevcut parola hatalı.',

  /*
   * ─── YAPILANDIRMA UÇLARI (Görev 3 / B adımı) ──────────────────────────────
   * Sunucunun döndürdüğü her kodun Türkçe karşılığı BURADA. Ekranlar (D ve E
   * adımları) hata metnini kendileri yazmaz — aynı kod iki ekranda iki farklı
   * cümleye dönüşmesin.
   */
  // Kapsamsız operatör: hata değil, eksik adım. `firmaSecilmedi` ile ayrılır.
  no_active_firm:
    'Önce bir firma seçin. Profil sayfasından firma seçtikten sonra kayıt yapabilirsiniz.',
  addr_code_exists: 'Bu saha kodu firmanızda zaten kullanılıyor.',
  panel_serial_exists: 'Bu pano seri numarası zaten kayıtlı.',
  tank_serial_exists: 'Bu tank seri numarası zaten kayıtlı.',
  tank_slot_taken: 'Sahada bu tank numarası zaten dolu.',
  // Var olmayan ya da BAŞKA FİRMAYA ait kayıt — ikisi de aynı cevabı verir
  // [K-021]: "yok" ile "sizin değil" ayrımı firma varlığını sızdırırdı.
  invalid_reference: 'Seçilen kayıt bulunamadı.',
  site_not_found: 'Saha bulunamadı.',
  customer_not_found: 'Müşteri bulunamadı.',
  incomplete_coordinates: 'Konum için enlem ve boylamın ikisi de gerekli.',
  no_changes: 'Değişiklik yapılmadı.',
};

function mesaj(code: string, status: number): string {
  const bilinen = METINLER[code];
  if (bilinen !== undefined) return bilinen;
  if (status >= 500) return 'Sunucuda bir sorun oluştu. Lütfen tekrar deneyin.';
  return 'İstek tamamlanamadı.';
}

/** Ağ tamamen erişilemezse — sunucu hatası DEĞİL, bağlantı yok. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edin.', { cause });
    this.name = 'NetworkError';
  }
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * OTURUM ORTA YERDE DÜŞERSE — S37
 *
 * `RequireSession` oturumu yalnız KENDİ sorgusundan bilir. Kullanıcı bir
 * ekranda dururken oturum sunucuda düşerse (süre dolması, yönetici oturumu
 * kapatması, sunucu yeniden başlaması) o sorgu kendiliğinden tekrar çalışmaz
 * — `staleTime: 0` yalnız "sorulursa taze sor" demektir, `refetchOnWindowFocus`
 * da kapalı. Sonuç: kullanıcının bastığı her düğme 401 alır, ekranda
 * "Oturumunuz sona erdi" yazar ve HİÇBİR ŞEY OLMAZ. Çıkış yolu yok.
 *
 * Bu yüzden 401 bilgisi buradan yukarı bildirilir; bağlama işini
 * `auth/expiry.ts` yapar (bu dosya React'i tanımaz).
 *
 * ─── MUAF YOLLAR ────────────────────────────────────────────────────────────
 * `/auth/me`, `/auth/login` ve `/auth/logout` için 401 bir OLAY değil, bir
 * CEVAPTIR: "giriş yapılmamış". Muaf tutulmasalardı açılıştaki her `/auth/me`
 * 401'i "oturum düştü" diye bildirilir, olmayan bir olay için ekran tazelenir
 * ve bildirim gerçekten anlamlı olduğu durumdan ayırt edilemezdi.
 * ═══════════════════════════════════════════════════════════════════════════
 */
type OturumDinleyici = () => void;

const dinleyiciler = new Set<OturumDinleyici>();

const MUAF_YOLLAR: ReadonlySet<string> = new Set(['/auth/me', '/auth/login', '/auth/logout']);

/**
 * Oturum düştüğünde haber alır. Dönüş değeri aboneliği bırakır — çağıran
 * (`main.tsx` ya da test) temizlemekle yükümlü, aksi hâlde testler arasında
 * ölü dinleyiciler birikir.
 */
export function oturumDustugunde(dinleyici: OturumDinleyici): () => void {
  dinleyiciler.add(dinleyici);
  return () => {
    dinleyiciler.delete(dinleyici);
  };
}

function haberVer(yol: string): void {
  // Sorgu dizesi atılır: `/sahalar?sayfa=2` ile `/sahalar` aynı yoldur.
  const yolAdi = yol.split('?')[0] ?? yol;
  if (MUAF_YOLLAR.has(yolAdi)) return;

  // Kopya üzerinde geziliyor: dinleyici kendi aboneliğini bırakabilsin.
  for (const dinleyici of [...dinleyiciler]) {
    try {
      dinleyici();
    } catch (error) {
      // Bir dinleyicinin patlaması İSTEĞİN hatasını yutmamalı: çağıran
      // 401'i görmeye devam etmeli.
      console.error('oturum düştü dinleyicisi hata verdi', error);
    }
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

/**
 * İstek gönderir ve gövdeyi çözer.
 *
 * Dönüş tipi ÇAĞIRANIN sorumluluğunda: burada `as T` yapılıyor çünkü
 * doğrulama zod şemalarıyla çağrı yerinde yapılacak [K-020] — sunucu ve
 * istemci aynı şemayı paylaştığı için tek doğruluk kaynağı orası.
 */
export async function istek<T>(yol: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  let response: Response;
  try {
    response = await fetch(`/api${yol}`, {
      method,
      // Opak oturum çerezi [K-006] — aynı köken olduğu için `SameSite` korur.
      credentials: 'include',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error) {
    // `fetch` yalnız AĞ hatasında fırlatır; 4xx/5xx fırlatmaz.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError(error);
  }

  if (!response.ok) {
    let kod = 'unknown';
    try {
      const gövde = (await response.json()) as ErrorBody;
      if (typeof gövde.error === 'string') kod = gövde.error;
    } catch {
      /* gövde JSON değil — kod `unknown` kalır */
    }
    const hata = new ApiError(response.status, kod);
    // Önce haber ver, sonra fırlat: çağıranın `catch`i çalışmadan önce oturum
    // durumu düzeltilmiş olur — ekran "sona erdi" metnini gösterip orada
    // kalmaz, yönlendirme başlar.
    if (hata.oturumsuz) haberVer(yol);
    throw hata;
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
